import { OptionPair } from "@/types/productTypes";
import { produce } from "immer";

import fetchDb from "../utils/customRequestFunction";
import { getStage } from "../utils/getFunction";

import { AdminSliceCreator, AdminStoreState } from "./useAdminStore";

export interface OptionPairStoreState {
   currentOptionPair: OptionPair | null;
   optionPairs: OptionPair[];
   refreshGraphData: () => Promise<void>;
   fetchOptionPair: (stageId: number) => Promise<void>;
   setCurrentOptionPair: (optionPair: OptionPair | null) => void;
   fetchOptionPairs: () => Promise<void>;
   createOptionPair: (
      productId: number,
      stageId: number,
      name: string,
      optionIds: number[]
   ) => Promise<void>;
   selectOptionPair: (
      productId: number,
      stageId: number,
      optionPairId: number
   ) => Promise<void>;
   cloneOptionPair: (
      productId: number,
      stageId: number,
      baseOptionPairId: number,
      name: string,
      optionIds: number[]
   ) => Promise<void>;
}

export const createOptionPairSlice: AdminSliceCreator<OptionPairStoreState> = (
   set,
   get
) => ({
   currentOptionPair: null,
   optionPairs: [],

   setCurrentOptionPair: (optionPair) => {
      set(
         produce((state: AdminStoreState) => {
            state.currentOptionPair = optionPair;
         })
      );
   },

   fetchOptionPairs: async () => {
      try {
         const fetchedOptionPairs = await fetchDb.get("/api/option-pairs");
         set(
            produce((state: AdminStoreState) => {
               state.optionPairs = fetchedOptionPairs;
               console.log("fetchedOptionPairs", fetchedOptionPairs);
            })
         );
      } catch (error) {
         console.error("Error fetching option pairs:", error);
      }
   },

   fetchOptionPair: async (stageId) => {
      console.log("fetchedOptionPair", stageId);
      try {
         const response = (await fetchDb.get(
            `/api/option-pair/stage/${stageId}`
         )) as OptionPair;
         console.log("response", response);
         set(
            produce((state: AdminStoreState) => {
               state.currentOptionPair = response;
            })
         );
      } catch (error) {
         set(
            produce((state: AdminStoreState) => {
               state.currentOptionPair = null;
            })
         );
         console.error("Error fetching option pair:", error);
      }
   },

   refreshGraphData: async () => {
      const optionPairId = get().currentOptionPair?.id;
      if (!optionPairId) return;

      try {
         const response = await fetchDb.get<OptionPair>(
            `/api/option-pair/${optionPairId}`
         );

         set(
            produce((state: AdminStoreState) => {
               state.currentOptionPair = response;
            })
         );

         await get().fetchGraphData();
      } catch (error) {
         console.error("Error refreshing graph data:", error);
      }
   },

   createOptionPair: async (productId, stageId, name, optionIds) => {
      const newOptionPairData = { name, stageId, optionIds };
      try {
         const createdOptionPair = await fetchDb.post("/api/option-pair", {
            ...newOptionPairData,
         });
         if (createdOptionPair) {
            await get().fetchProducts?.();
            await get().fetchConfigs?.(productId);
            await get().fetchAllOptions?.(productId);
         }
      } catch (error) {
         console.error("Error creating option pair:", error);
      }
   },

   selectOptionPair: async (productId, stageId, optionPairId) => {
      set(
         produce((state) => {
            const stage = getStage(state.allProducts, productId, stageId);
            if (!stage) return;
            stage.optionPairId = optionPairId;
            stage.optionPair =
               get().optionPairs.find((pair) => pair.id === optionPairId) ??
               undefined;
         })
      );
      await fetchDb.patch(`/api/option-pair/stage/${stageId}`, {
         optionId: optionPairId,
      });
   },

   cloneOptionPair: async (
      productId,
      stageId,
      baseOptionPairId,
      name,
      optionIds
   ) => {
      try {
         const payload = {
            baseOptionPairId,
            newOptionIds: optionIds,
            stageId,
            name,
         };
         const clonedOptionPair = await fetchDb.post(
            "/api/option-pair/clone",
            payload
         );
         if (clonedOptionPair) {
            await get().fetchProducts?.();
            await get().fetchConfigs?.(productId);
            await get().fetchAllOptions?.(productId);
         }
      } catch (error) {
         console.error("Error cloning option pair:", error);
      }
   },
});
